import React from "react";
import { Box, Typography, Chip, Stack, Divider } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HistoryIcon from "@mui/icons-material/History";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";

// doc 29, Phase 9 — P9.9 (eval surfacing).
//
// Pure presentational card. Surfaces, per movement:
//   1) the prescription `source` (was the next session prescribed by the
//      progression engine?) + the engine's `reasoning` string, from the
//      contract-v2 /api/exercise-prefill payload.
//   2) the override-rate eval metric (doc 05 trust metric) — the share of
//      prescribed sets whose final actual_* deviated from target_* beyond a
//      small epsilon. Renders a clear "data pending" state when the underlying
//      target-vs-actual per-set data is not yet web-reachable (see Follow-ups).
//
// Kept prop-driven (no redux/axios import) so it renders in tests without the
// full app tree.

// Shared, exported so the admin eval page (PrescriptionsDashboard) labels
// `source` identically to the in-app per-movement card.
export const SOURCE_META = {
  progression: {
    label: "Progression engine",
    color: "success",
    icon: <AutoAwesomeIcon fontSize="small" />,
    blurb: "Next session was prescribed by the progressive-overload engine.",
  },
  set_logs: {
    label: "Last performance (per-set)",
    color: "info",
    icon: <HistoryIcon fontSize="small" />,
    blurb: "Copied from your last logged per-set performance — no nudge applied.",
  },
  scalar_history: {
    label: "Last performance (scalar)",
    color: "info",
    icon: <HistoryIcon fontSize="small" />,
    blurb: "Copied from your last logged sets/reps/weight — no nudge applied.",
  },
  default: {
    label: "No history",
    color: "default",
    icon: <HelpOutlineIcon fontSize="small" />,
    blurb: "Cold start — empty scaffold, nothing prescribed yet.",
  },
};

const formatRate = (rate) => `${Math.round(rate * 100)}%`;

const PrescriptionEvalCard = ({ exerciseName, prescription, overrideRate }) => {
  const source = prescription?.source ?? null;
  const meta = (source && SOURCE_META[source]) || null;
  const reasoning = prescription?.reasoning ?? null;

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        p: 2.5,
        borderRadius: 2,
        width: "100%",
      }}
    >
      <Typography variant="overline" color="text.secondary">
        Prescription &amp; eval{exerciseName ? ` — ${exerciseName}` : ""}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Source:
        </Typography>
        {meta ? (
          <Chip
            size="small"
            color={meta.color}
            icon={meta.icon}
            label={meta.label}
          />
        ) : (
          <Chip size="small" label="Unknown" />
        )}
      </Stack>

      {meta?.blurb ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {meta.blurb}
        </Typography>
      ) : null}

      {reasoning ? (
        <Typography
          variant="body2"
          sx={{ fontStyle: "italic", mb: 1 }}
          data-testid="prescription-reasoning"
        >
          “{reasoning}”
        </Typography>
      ) : source === "progression" ? null : (
        <Typography variant="caption" color="text.secondary">
          No reasoning provided (only the progression engine emits one).
        </Typography>
      )}

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="overline" color="text.secondary">
        Override rate
      </Typography>

      {overrideRate?.status === "ready" ? (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            {formatRate(overrideRate.rate)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {overrideRate.overrides} of {overrideRate.total} prescribed sets
            deviated from target beyond epsilon
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 0.5 }} data-testid="override-rate-pending">
          <Chip size="small" color="warning" label="Data pending" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Override rate needs per-set target vs. final actual data. No current
            web endpoint exposes confirmed-set actuals — pending a worker
            endpoint (e.g. <code>GET /api/admin/override-rate</code> or
            extending exercise-stats with per-set target/actual). Computes
            automatically once that ships.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PrescriptionEvalCard;
